import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback } from 'react'
import { TouchableOpacity, StyleSheet, View, ScrollView } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useThrottledCallback } from 'use-debounce'
import { useFocusEffect, useRouter } from 'expo-router'

import { DS } from '../../design-system'
import { useCreateGame } from '../../hooks/use-create-game'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { GameModeTag } from '../../models/api-store'
import { getRouteToLastVisitedTab } from '../../navigation'
import createQuickGameImage from './assets/createquick.png'
import createTournamentImage from './assets/createtournament.png'
import { CreateGameCard } from './CreateGameCard'
import { CreateGameTitle } from './CreateGameTitle'
import { styles } from './styles'
import { WebWrapper } from '@/components/WebWrapper'

export const CreateGameScreen = observer(() => {
  const router = useRouter()
  const {
    game: { isPracticeMode, setPlayKind, setTableName, teardown, setGameKind: setGameType, clearWalkthrough },
    session: { isTeacher, user, selectedDailyTask, lastVisitedTab, updateDailyTaskStatus },
    analytics: { setEvent }
  } = useStore()
  const { setLoading, createGame } = useCreateGame()

  const [playerRole, setPlayerRole] = React.useState('player')

  useFocusEffect(
    useCallback(() => {
      teardown()
      setPlayKind(playerRole)
      setPlayerRole('player')
    }, [playerRole, setPlayKind, teardown])
  )

  const onNext = useThrottledCallback(async (gameType: GameModeTag) => {
    clearWalkthrough()
    setGameType(gameType)
    setTableName('') // TODO: is this necessary?
    if ([GameModeTag.default].includes(gameType)) {
      setEvent(events.game.create_quick_game_tapped, {
        userId: user?._id.toString()
      })
      if (isTeacher && !isPracticeMode && gameType !== GameModeTag.threeCard) {
        router.push('/game/create/user-role')
      } else {
        try {
          await createGame({ gameKind: gameType })
        } catch (error) {
          console.error(error)
          setLoading(false)
        }
      }
    }
  }, 1000)

  const createScheduledTournament = useCallback(async () => {
    clearWalkthrough()
    setGameType(GameModeTag.tournament)
    setTableName('')
    setEvent(events.game.create_tournament_tapped, {
      userId: user?._id.toString()
    })
    router.push(`/game/create/user-role?gameKind=${GameModeTag.tournament}&flow=scheduled`)
  }, [clearWalkthrough, setGameType, setTableName, setEvent, router])

  const onBack = useThrottledCallback(
    () => {
      if (router.canDismiss()) {
        return router.dismiss()
      }
      if (router.canGoBack()) {
        return router.canGoBack()
      }
      return router.dismissTo(getRouteToLastVisitedTab(lastVisitedTab))
    },
    350,
    { leading: true }
  )

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
      <WebWrapper>
        <DS.Frame style={styles.body}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <DS.Icon name="Close" size={24} color="Global/White" />
          </TouchableOpacity>
          <DS.Frame gap="24">
            <CreateGameTitle />
            <ScrollView
              style={containerStyles.scrollView}
              contentContainerStyle={containerStyles.buttonsContainer}
              showsVerticalScrollIndicator={false}
            >
              <View style={containerStyles.button}>
                <CreateGameCard
                  title={translate('createGameScreen.quickGame.title')}
                  items={[
                    translate('createGameScreen.quickGame.item1'),
                    translate('createGameScreen.quickGame.item2'),
                    translate('createGameScreen.quickGame.item3')
                  ]}
                  image={createQuickGameImage}
                  onPress={() => onNext(GameModeTag.default)}
                />
              </View>
              <View style={containerStyles.button}>
                <CreateGameCard
                  title={translate('createGameScreen.custom.title')}
                  items={[
                    translate('createGameScreen.custom.item1'),
                    translate('createGameScreen.custom.item2'),
                    translate('createGameScreen.custom.item3')
                  ]}
                  image={createTournamentImage}
                  onPress={createScheduledTournament}
                />
              </View>
            </ScrollView>
          </DS.Frame>
        </DS.Frame>
      </WebWrapper>
    </SafeAreaView>
  )
})

const containerStyles = StyleSheet.create({
  scrollView: {
    flexGrow: 1
  },
  buttonsContainer: {
    alignItems: 'center',
    paddingBottom: 24
  },
  button: {
    width: '100%',
    maxWidth: 360,
    marginBottom: 8
  }
})
